// @flow

import React, { useEffect, useRef, useState, type Node } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import spine from '../../../lib/spine-webgl.custom.js';

import bagAtlas from 'assets/animations/bag/bag.atlas';
import bagJson from 'assets/animations/bag/bag.json';
import bagPng from 'assets/animations/bag/bag.png';

import bankAtlas from 'assets/animations/bank/bank.atlas';
import bankJson from 'assets/animations/bank/bank.json';
import bankPng from 'assets/animations/bank/bank.png';

import footballAtlas from 'assets/animations/football/football.atlas';
import footballJson from 'assets/animations/football/football.json';
import footballPng from 'assets/animations/football/football.png';

import loaderAtlas from 'assets/animations/loader/loader.atlas';
import loaderJson from 'assets/animations/loader/loader.json';
import loaderPng from 'assets/animations/loader/loader.png';

import pigAtlas from 'assets/animations/pig/pig.atlas';
import pigJson from 'assets/animations/pig/pig.json';
import pigPng from 'assets/animations/pig/pig.png';

import safeAtlas from 'assets/animations/safe/safe.atlas';
import safeJson from 'assets/animations/safe/safe.json';
import safePng from 'assets/animations/safe/safe.png';

import tokenAtlas from 'assets/animations/token/token.atlas';
import tokenJson from 'assets/animations/token/token.json';
import tokenPng from 'assets/animations/token/token.png';

import truckAtlas from 'assets/animations/truck/truck.atlas';
import truckJson from 'assets/animations/truck/truck.json';
import truckPng from 'assets/animations/truck/truck.png';

import walletAtlas from 'assets/animations/wallet/wallet.atlas';
import walletJson from 'assets/animations/wallet/wallet.json';
import walletPng from 'assets/animations/wallet/wallet.png';

type SpinePlayerProps = {|
  className?: string,
  skeletonName: string,
  tracks?: string[],
  current?: number,
  controls?: boolean,
  fallback?: Node,
  autoPlay?: boolean,
  singleSpine?: boolean,
|};

type Skeleton = {|
  skeleton: any,
  state: any,
  bounds: any,
|};

const RawSpinePlayer = ({
  className,
  skeletonName,
  tracks,
  current,
  controls,
  fallback,
  autoPlay,
  singleSpine,
}: SpinePlayerProps) => {
  const [showFallback, setShowFallback] = useState(false);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const currentAnimation = useRef(0);
  const animations = useRef<string[]>([]);
  const [animationsState, setAnimationsState] = useState<string[]>([]);
  const direction = useRef('forward');
  const rafRef = useRef();
  const animationState = useRef<any>();
  let lastFrameTime = Date.now() / 1000;
  let shader: any;
  let batcher: any;
  let gl: ?WebGLRenderingContext = null;
  const mvp = new spine.webgl.Matrix4();
  let assetManager: any;
  let skeletonRenderer: any;
  const skeleton = useRef<Skeleton>({
    skeleton: null,
    state: null,
    bounds: null,
  });
  const devicePixelRatio = window.devicePixelRatio || 1;

  const [spineAtlas, setSpineAtlas] = useState('');
  const [spineImage, setSpineImage] = useState('');
  const [spineJson, setSpineJson] = useState('');

  useEffect(() => {
    switch (skeletonName) {
      case 'bag':
        setSpineAtlas(bagAtlas);
        setSpineJson(bagJson);
        setSpineImage(bagPng);
        break;
      case 'bank':
        setSpineAtlas(bankAtlas);
        setSpineJson(bankJson);
        setSpineImage(bankPng);
        break;
      case 'football':
        setSpineAtlas(footballAtlas);
        setSpineJson(footballJson);
        setSpineImage(footballPng);
        break;
      case 'loader':
        setSpineAtlas(loaderAtlas);
        setSpineJson(loaderJson);
        setSpineImage(loaderPng);
        break;

      case 'pig':
        setSpineAtlas(pigAtlas);
        setSpineJson(pigJson);
        setSpineImage(pigPng);
        break;
      case 'safe':
        setSpineAtlas(safeAtlas);
        setSpineJson(safeJson);
        setSpineImage(safePng);
        break;
      case 'token':
        setSpineAtlas(tokenAtlas);
        setSpineJson(tokenJson);
        setSpineImage(tokenPng);
        break;
      case 'truck':
        setSpineAtlas(truckAtlas);
        setSpineJson(truckJson);
        setSpineImage(truckPng);
        break;
      case 'wallet':
        setSpineAtlas(walletAtlas);
        setSpineJson(walletJson);
        setSpineImage(walletPng);
        break;
      default:
        break;
    }
  }, [skeletonName]);

  const calculateBounds = (skel) => {
    skel.setToSetupPose();
    skel.updateWorldTransform();
    const offset = new spine.Vector2();
    const size = new spine.Vector2();
    skel.getBounds(offset, size, []);
    return { offset, size };
  };

  const loadSkeleton = () => {
    const skin = 'default';
    const atlas = new spine.TextureAtlas(assetManager.get(spineAtlas), () => {
      return assetManager.get(spineImage);
    });
    const atlasLoader = new spine.AtlasAttachmentLoader(atlas);
    const skeletonJson = new spine.SkeletonJson(atlasLoader);
    const skeletonData = skeletonJson.readSkeletonData(spineJson);
    let parsedAnimations = animations.current;
    if (animations.current.length === 0) {
      parsedAnimations = skeletonData.animations.map((item) => item.name);
      animations.current = parsedAnimations;
      setAnimationsState(parsedAnimations);
    }

    const skel = new spine.Skeleton(skeletonData);
    skel.setSkinByName(skin);
    const bounds = calculateBounds(skel);
    const animationStateData = new spine.AnimationStateData(skel.data);
    animationState.current = new spine.AnimationState(animationStateData);
    animationState.current.setAnimation(0, parsedAnimations[current || 0], autoPlay);
    currentAnimation.current = current || 0;

    return { skeleton: skel, state: animationState.current, bounds };
  };

  const gotoTrack = (track: number, setCurrent = true) => {
    if (!singleSpine) {
      if (animations.current.length === 0) return;
      animationState.current.setAnimation(0, animations.current[0], false);
      const trackEntry = skeleton.current.state.getCurrent(0);
      trackEntry.animationStart = 0;
      direction.current = 'forward';
      return;
    }

    if (animations.current.length === 0 || track === currentAnimation.current) return;

    animationState.current.setAnimation(
      0,
      animations.current[track < currentAnimation.current ? track + 1 : track],
      false
    );

    const trackEntry = skeleton.current.state.getCurrent(0);

    if (track < currentAnimation.current) {
      trackEntry.animationStart = trackEntry.animationEnd;
      direction.current = 'backward';
    } else {
      direction.current = 'forward';
    }

    if (setCurrent) {
      currentAnimation.current = track;
    }
  };

  const resize = () => {
    if (!canvas.current || !gl) return;
    const canvasElement = canvas.current;

    const w = canvasElement.clientWidth;
    const h = canvasElement.clientHeight;

    canvasElement.width = w * devicePixelRatio;
    canvasElement.height = h * devicePixelRatio;

    const { bounds } = skeleton.current;
    const centerX = bounds.offset.x + bounds.size.x / 2;
    const centerY = bounds.offset.y + bounds.size.y / 2;
    const scaleX = (bounds.size.x / canvasElement.width) * devicePixelRatio;
    const scaleY = (bounds.size.y / canvasElement.height) * devicePixelRatio;
    let scale = Math.max(scaleX, scaleY) * 1.2;
    if (scale < 1) scale = 1;
    const width = (canvasElement.width * scale) / devicePixelRatio;
    const height = (canvasElement.height * scale) / devicePixelRatio;

    mvp.ortho2d(centerX - width / 2, centerY - height / 2, width, height);
    gl.viewport(
      bounds.offset.x + bounds.size.x / 2,
      bounds.offset.y + bounds.size.y / 2,
      canvasElement.width,
      canvasElement.height
    );
  };

  const render = () => {
    const now = Date.now() / 1000;
    const delta = now - lastFrameTime;
    lastFrameTime = now;

    resize();

    if (gl) {
      gl.clear(gl.COLOR_BUFFER_BIT);
    }

    const { state } = skeleton.current;
    const skel = skeleton.current.skeleton;

    const { animationLast } = state.getCurrent(0);

    // System to stop animation when arrived on desired track
    if (direction.current === 'backward' && animationLast !== -1) {
      if (animationLast <= 0.1) {
        gotoTrack(currentAnimation.current - 1, false);
        const trackEntry = skeleton.current.state.getCurrent(0);
        trackEntry.trackTime = trackEntry.trackEnd;
      }
    }

    // System to forward/backward the animation
    if (direction.current === 'forward') {
      state.timeScale = 1;
    } else if (direction.current === 'backward') {
      state.timeScale = -1;
    }

    state.update(delta);
    state.apply(skel);
    skel.updateWorldTransform();

    shader.bind();
    shader.setUniformi(spine.webgl.Shader.SAMPLER, 0);
    shader.setUniform4x4f(spine.webgl.Shader.MVP_MATRIX, mvp.values);
    batcher.begin(shader);
    skeletonRenderer.premultipliedAlpha = true;
    skeletonRenderer.draw(batcher, skel);
    batcher.end();
    shader.unbind();

    rafRef.current = requestAnimationFrame(render);
  };

  const load = () => {
    if (assetManager.isLoadingComplete()) {
      skeleton.current = loadSkeleton();
      rafRef.current = requestAnimationFrame(render);
    } else {
      rafRef.current = requestAnimationFrame(load);
    }
  };

  const gotoPrevious = () => {
    if (currentAnimation.current > 0) {
      gotoTrack(currentAnimation.current - 1);
    }
  };

  const gotoNext = () => {
    if (animations.current && currentAnimation.current < animations.current.length - 1) {
      gotoTrack(currentAnimation.current + 1);
    }
  };

  const renderControlsSteps = () => {
    if (!animationsState) return '';

    const items = [];
    for (let i = 0; i < animationsState.length; i += 1) {
      items.push(
        <li className="controls-item" key={i}>
          <button type="button" onClick={() => gotoTrack(i)}>
            {i + 1}
          </button>
        </li>
      );
    }

    return items;
  };

  const renderControls = () => {
    if (!controls) return '';

    return (
      <nav className="controls">
        <ul className="controls-list">
          <li className="controls-item">
            <button type="button" onClick={gotoPrevious}>
              {' '}
              &lt;{' '}
            </button>
          </li>
          {renderControlsSteps()}
          <li className="controls-item">
            <button type="button" onClick={gotoNext}>
              {' '}
              &gt;{' '}
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  useEffect(() => {
    if (current || current === 0) {
      gotoTrack(current);
    }
  }, [current]);

  useEffect(() => {
    if (!canvas.current || !spineAtlas || !spineImage || !spineJson) {
      return setShowFallback(true);
    }

    const canvasElement = canvas.current;
    setShowFallback(false);

    if (!shader) {
      const config = { alpha: true };
      gl =
        canvasElement.getContext('webgl', config) ||
        canvasElement.getContext('experimental-webgl', config);
      if (!gl || skeletonName === '') {
        return setShowFallback(true);
      }
      shader = spine.webgl.Shader.newTwoColoredTextured(gl);
      batcher = new spine.webgl.PolygonBatcher(gl);
      mvp.ortho2d(0, 0, canvasElement.width - 1, canvasElement.height - 1);
      skeletonRenderer = new spine.webgl.SkeletonRenderer(gl);
      // assetManager = new spine.webgl.AssetManager(gl, '../../../assets/');
      assetManager = new spine.webgl.AssetManager(gl);
      assetManager.loadText(spineAtlas);
      assetManager.loadTexture(spineImage);

      rafRef.current = requestAnimationFrame(load);
    }

    if (tracks) {
      animations.current = tracks;
      setAnimationsState(tracks);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [tracks, spineAtlas, spineImage, spineJson]);

  return (
    <div className={className}>
      {showFallback && fallback}
      <canvas className={`canvas${showFallback ? ' hidden' : ''}`} ref={canvas} />
      {renderControls()}
    </div>
  );
};

RawSpinePlayer.defaultProps = {
  className: null,
  tracks: [],
  current: 0,
  controls: false,
  fallback: null,
  autoPlay: false,
  singleSpine: false,
};

export const SpinePlayer: StyledComponent<SpinePlayerProps, {}, typeof RawSpinePlayer> = styled(
  RawSpinePlayer
)`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;

  .canvas {
    width: 100%;
    height: 100%;

    &.hidden {
      display: none;
    }
  }

  img {
    max-width: 100%;
    max-height: 100%;
  }

  .controls {
    position: absolute;
    bottom: 0;
    width: 100%;
  }

  .controls-list {
    display: flex;
    justify-content: center;
    margin: 0;
  }
`;
